import React, { useEffect, useState } from 'react';
import {
  getBillboardDemographics,
  type DemographicsResponse,
  type AgeGroupCounts,
  type GenderCounts,
} from '../../api/billboard';

interface AudienceDemographicsProps {
  deviceId?: string;
  /** Filter demographics to a specific ad */
  adId?: string;
  /** How many hours of data to aggregate (default 1) */
  hours?: number;
  /** Poll interval in ms (0 = no polling, default 10000) */
  pollInterval?: number;
}

const AGE_LABELS: (keyof AgeGroupCounts)[] = [
  '0-17', '18-24', '25-34', '35-44', '45-54', '55-64', '65+',
];

const AGE_COLORS: Record<string, string> = {
  '0-17': '#a78bfa',
  '18-24': '#60a5fa',
  '25-34': '#34d399',
  '35-44': '#fbbf24',
  '45-54': '#f97316',
  '55-64': '#f87171',
  '65+': '#e879f9',
};

const GENDER_COLORS: Record<keyof GenderCounts, string> = {
  male: '#60a5fa',
  female: '#f472b6',
  unknown: '#94a3b8',
};

const cardStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderRadius: 12,
  background: '#0f172a',
  color: '#e5e7eb',
  flex: '1 1 200px',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  opacity: 0.7,
  marginBottom: 2,
};

const valueStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 700,
};

export const AudienceDemographics: React.FC<AudienceDemographicsProps> = ({
  deviceId,
  adId,
  hours = 1,
  pollInterval = 10_000,
}) => {
  const [data, setData] = useState<DemographicsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetch = async () => {
      try {
        const result = await getBillboardDemographics(deviceId, hours, adId);
        if (!cancelled) {
          setData(result);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to load demographics',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetch();

    if (pollInterval > 0) {
      const id = setInterval(fetch, pollInterval);
      return () => { cancelled = true; clearInterval(id); };
    }
    return () => { cancelled = true; };
  }, [deviceId, adId, hours, pollInterval]);

  if (loading) {
    return (
      <div style={{ padding: '1rem', color: '#94a3b8' }}>
        Loading audience demographics…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '1rem', color: '#94a3b8', fontSize: 13 }}>
        Audience data not available yet. Camera feeds will populate demographics once a device agent is connected.
      </div>
    );
  }

  if (!data || data.totalPeople === 0) {
    return (
      <div style={{ padding: '1rem', color: '#94a3b8', fontSize: 13 }}>
        No audience data available yet. Demographics will appear once a billboard device with a camera agent starts sending inference data.
      </div>
    );
  }

  const maxAge = Math.max(...AGE_LABELS.map((k) => data.ageGroups[k] ?? 0), 1);
  const totalGender = data.gender.male + data.gender.female + data.gender.unknown || 1;

  return (
    <div>
      <h3 style={{ marginBottom: '0.75rem' }}>Audience Demographics</h3>
      <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: '0.75rem' }}>
        Privacy-safe aggregate counts — no facial data is stored.
      </p>

      {/* Summary cards */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
        <div style={cardStyle}>
          <div style={labelStyle}>Total people detected</div>
          <div style={valueStyle}>{data.totalPeople.toLocaleString()}</div>
        </div>
        <div style={cardStyle}>
          <div style={labelStyle}>Dominant age range</div>
          <div style={valueStyle}>{data.dominantAgeRange}</div>
        </div>
        <div style={cardStyle}>
          <div style={labelStyle}>Dominant gender</div>
          <div style={valueStyle} className="capitalize">{data.dominantGender}</div>
        </div>
      </div>

      {/* Age distribution bar chart */}
      <div style={{ ...cardStyle, marginBottom: '1rem', flex: 'unset' }}>
        <div style={{ fontSize: 12, opacity: 0.8, marginBottom: '0.5rem' }}>
          Age distribution
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {AGE_LABELS.map((age) => {
            const count = data.ageGroups[age] ?? 0;
            const pct = maxAge > 0 ? (count / maxAge) * 100 : 0;
            return (
              <div key={age} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 40, fontSize: 11, textAlign: 'right', flexShrink: 0 }}>
                  {age}
                </span>
                <div style={{ flex: 1, height: 14, borderRadius: 4, background: '#1e293b', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${pct}%`,
                      background: AGE_COLORS[age],
                      borderRadius: 4,
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
                <span style={{ width: 32, fontSize: 11, textAlign: 'right', flexShrink: 0 }}>
                  {count}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Gender breakdown */}
      <div style={{ ...cardStyle, marginBottom: '1rem', flex: 'unset' }}>
        <div style={{ fontSize: 12, opacity: 0.8, marginBottom: '0.5rem' }}>
          Gender breakdown
        </div>
        <div style={{ display: 'flex', height: 20, borderRadius: 6, overflow: 'hidden' }}>
          {(['male', 'female', 'unknown'] as const).map((g) => {
            const count = data.gender[g];
            const pct = (count / totalGender) * 100;
            if (pct === 0) return null;
            return (
              <div
                key={g}
                style={{
                  width: `${pct}%`,
                  background: GENDER_COLORS[g],
                  transition: 'width 0.3s ease',
                }}
                title={`${g}: ${count} (${pct.toFixed(1)}%)`}
              />
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: '1rem', marginTop: 6, fontSize: 11 }}>
          {(['male', 'female', 'unknown'] as const).map((g) => (
            <span key={g} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: GENDER_COLORS[g], display: 'inline-block' }} />
              {g}: {data.gender[g]} ({((data.gender[g] / totalGender) * 100).toFixed(0)}%)
            </span>
          ))}
        </div>
      </div>

      {/* Objects detected */}
      {data.objectsDetected && Object.keys(data.objectsDetected).length > 0 && (
        <div style={{ ...cardStyle, flex: 'unset' }}>
          <div style={{ fontSize: 12, opacity: 0.8, marginBottom: '0.5rem' }}>
            Objects detected
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {Object.entries(data.objectsDetected)
              .sort(([, a], [, b]) => b - a)
              .map(([label, count]) => (
                <span
                  key={label}
                  style={{
                    padding: '3px 10px',
                    borderRadius: 20,
                    background: '#1e293b',
                    fontSize: 12,
                  }}
                >
                  {label}: {count}
                </span>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AudienceDemographics;
